import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Search, Copy, ExternalLink, KeyRound, ChevronDown,
  User, MapPin, FileText, Users, Upload, Download, FileSpreadsheet, MessageCircle,
  FolderOpen, Sparkles
} from "lucide-react";
import { downloadAuthFile } from "@/components/WhatsAppDialog";
import ClientDocs from "@/components/ClientDocs";
import AIClientInsight from "@/components/AIClientInsight";

const SERVICES = ["GST", "ITR", "TDS", "ROC", "Audit", "Payroll"];
const CONSTITUTIONS = ["Proprietorship", "Partnership", "LLP", "Private Limited", "Public Limited", "HUF", "Trust", "Society", "Government"];
const BUSINESS_ACTIVITIES = ["Retail", "Wholesale", "Manufacturing", "Service", "Mixed", "Import/Export"];
const RETURN_TYPES = ["GSTR-1", "GSTR-3B", "GSTR-1A", "CMP-08", "GSTR-4", "GSTR-7", "GSTR-9", "ITC-04"];
const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra",
  "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim",
  "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

const serviceColor = {
  GST: "bg-[#EFF4FF] text-[#002FA7] border-[#002FA7]",
  ITR: "bg-zinc-900 text-white border-zinc-900",
  TDS: "bg-[#FFFAEB] text-[#B54708] border-[#F79009]",
  ROC: "bg-[#F5F3FF] text-[#7C3AED] border-[#7C3AED]",
  Audit: "bg-[#FEF3F2] text-[#B42318] border-[#D92D20]",
  Payroll: "bg-[#ECFDF3] text-[#027A48] border-[#039855]",
};

const PORTALS = {
  gst: { url: "https://www.gst.gov.in", name: "GST Portal", userKey: "gst_username", passKey: "gst_password" },
  itr: { url: "https://www.incometax.gov.in", name: "Income Tax", userKey: "itr_username", passKey: "itr_password" },
  traces: { url: "https://www.tdscpc.gov.in", name: "TRACES", userKey: "traces_username", passKey: "traces_password" },
  mca: { url: "https://www.mca.gov.in", name: "MCA / ROC", userKey: "mca_username", passKey: "mca_password" },
};

const empty = {
  name: "", type: "Individual", trade_name: "", branch_name: "", constitution: "",
  pan: "", gstin: "", gst_registration_date: "", commencement_date: "", business_activity: "",
  email: "", phone: "", mobile: "", contact_person: "",
  flat: "", floor: "", premises: "", street: "", locality: "", city: "", state: "", pin: "", address: "",
  return_period: "Monthly", returns_applicable: [], ward_sector_circle: "",
  is_composition: false, possession: "",
  group: "", sub_group: "", file_no: "", remark: "",
  services: [], notes: "",
  gst_username: "", gst_password: "", itr_username: "", itr_password: "",
  traces_username: "", traces_password: "", mca_username: "", mca_password: "",
};

const TABS = [
  { key: "profile", label: "Profile", icon: User },
  { key: "address", label: "Address", icon: MapPin },
  { key: "gst", label: "GST / Returns", icon: FileText },
  { key: "logins", label: "Portal Logins", icon: KeyRound },
  { key: "org", label: "Group / Notes", icon: Users },
  { key: "docs", label: "Documents", icon: FolderOpen },
  { key: "ai", label: "AI Insight", icon: Sparkles },
];

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("profile");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [credsOpenFor, setCredsOpenFor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => { const { data } = await api.get("/clients"); setClients(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setTab("profile"); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, services: c.services || [], returns_applicable: c.returns_applicable || [] }); setTab("profile"); setOpen(true); };
  const toggle = (key, val) => setForm(p => ({ ...p, [key]: (p[key] || []).includes(val) ? p[key].filter(x => x !== val) : [...(p[key] || []), val] }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form); else await api.post("/clients", form);
      toast.success("Client saved"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`); toast.success("Deleted"); load();
  };

  const copy = async (text, label) => { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  const openPortalWithCreds = async (client, portalKey) => {
    const p = PORTALS[portalKey];
    const u = client[p.userKey];
    if (u) { try { await navigator.clipboard.writeText(u); toast.success(`${p.name} username copied · paste in login`, { duration: 5000 }); } catch {} }
    window.open(p.url, "_blank", "noopener,noreferrer");
  };

  const downloadTemplate = async () => {
    try {
      await downloadAuthFile("/clients/import/template", "taxinpho-clients-template.csv");
      toast.success("Template downloaded");
    } catch (e) { toast.error("Could not download template"); }
  };

  const doImport = async () => {
    if (!importFile) { toast.error("Choose a CSV or Excel file first"); return; }
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (data.inserted > 0) toast.success(`Imported ${data.inserted} clients`);
      if (data.skipped > 0) toast.error(`${data.skipped} rows skipped`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImportBusy(false);
    }
  };

  // Unique groups for filter dropdown
  const groups = useMemo(() => {
    const set = new Set(clients.map(c => c.group).filter(Boolean));
    return Array.from(set).sort();
  }, [clients]);

  const filtered = clients.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      const hay = [c.name, c.trade_name, c.pan, c.gstin, c.file_no, c.mobile, c.phone].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filter !== "all" && !(c.services || []).includes(filter)) return false;
    if (groupFilter !== "all" && c.group !== groupFilter) return false;
    return true;
  });

  const counts = SERVICES.reduce((acc, s) => { acc[s] = clients.filter(c => (c.services || []).includes(s)).length; return acc; }, {});

  const fld = (label, val, onChange, props = {}) => {
    const { upper, ...rest } = props;
    return (
      <div>
        <label className="label-caps block mb-1.5 text-[10px]">{label}</label>
        <input value={val || ""} onChange={e => onChange(upper ? e.target.value.toUpperCase() : e.target.value)} {...rest} className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900" />
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps mb-2">Directory</p>
          <h1 className="font-display font-black text-4xl tracking-tighter">Clients</h1>
          <p className="text-zinc-600 mt-2">{clients.length} clients · filter by service or group.</p>
        </div>
        {can(user, "clients_write") && (
          <div className="flex items-center gap-2">
            <button onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} data-testid="import-clients" className="border border-zinc-900 text-zinc-900 px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-zinc-100">
              <FileSpreadsheet size={16} /> Import Excel / CSV
            </button>
            <button onClick={openNew} data-testid="new-client" className="bg-zinc-900 text-white px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-zinc-800"><Plus size={16} /> New Client</button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 text-sm font-semibold border ${filter === "all" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>All <span className="font-mono ml-1 opacity-70">{clients.length}</span></button>
        {SERVICES.map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`px-3 py-1.5 text-sm font-semibold border ${filter === s ? serviceColor[s] : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>{s} <span className="font-mono ml-1 opacity-70">{counts[s] || 0}</span></button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, PAN, GSTIN, file no…" data-testid="search-clients" className="border border-zinc-300 pl-10 pr-3 py-2 focus:outline-none focus:border-zinc-900 bg-white w-80" />
        </div>
        {groups.length > 0 && (
          <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} className="border border-zinc-300 px-3 py-2 bg-white text-sm" data-testid="group-filter">
            <option value="all">All Groups</option>
            {groups.map(g => <option key={g}>{g}</option>)}
          </select>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left px-6 py-3 label-caps">Name</th>
              <th className="text-left px-6 py-3 label-caps">PAN / GSTIN</th>
              <th className="text-left px-6 py-3 label-caps">Group</th>
              <th className="text-left px-6 py-3 label-caps">Services</th>
              <th className="text-left px-6 py-3 label-caps">Mobile</th>
              <th className="text-right px-6 py-3 label-caps">Portals</th>
              <th className="text-right px-6 py-3 label-caps"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-zinc-500 font-mono text-sm">[ no clients match ]</td></tr>}
            {filtered.map(c => {
              const credsCount = ["gst_username", "itr_username", "traces_username", "mca_username"].filter(k => c[k]).length;
              return (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50 align-top">
                  <td className="px-6 py-3">
                    <div className="font-medium">{c.name}</div>
                    {c.trade_name && <div className="text-xs text-zinc-500">{c.trade_name}</div>}
                    {c.file_no && <div className="text-[10px] font-mono text-zinc-400 mt-0.5">File #{c.file_no}</div>}
                  </td>
                  <td className="px-6 py-3 font-mono text-xs">
                    <div>{c.pan || "—"}</div>
                    <div className="text-zinc-500">{c.gstin || ""}</div>
                  </td>
                  <td className="px-6 py-3 text-sm">
                    {c.group ? (
                      <div>
                        <div className="font-medium">{c.group}</div>
                        {c.sub_group && <div className="text-xs text-zinc-500">{c.sub_group}</div>}
                      </div>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[180px]">
                      {(c.services || []).map(s => <span key={s} className={`inline-block px-2 py-0.5 text-[10px] font-semibold border ${serviceColor[s]}`}>{s}</span>)}
                      {(!c.services || c.services.length === 0) && <span className="text-xs text-zinc-400">—</span>}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm font-mono">{c.mobile || c.phone || "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="relative inline-block">
                      <button onClick={() => setCredsOpenFor(credsOpenFor === c.id ? null : c.id)} data-testid={`portals-${c.id}`} className={`inline-flex items-center gap-1 px-2.5 py-1 border ${credsCount > 0 ? "bg-[#EFF4FF] text-[#002FA7] border-[#002FA7]" : "bg-zinc-50 text-zinc-500 border-zinc-300"} text-xs font-semibold hover:opacity-80`}>
                        <KeyRound size={12} /> {credsCount > 0 ? `${credsCount} saved` : "No creds"} <ChevronDown size={12} />
                      </button>
                      {credsOpenFor === c.id && (
                        <div className="absolute right-0 mt-1 z-20 w-72 bg-white border border-zinc-300 shadow-lg" onClick={e => e.stopPropagation()}>
                          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                            <p className="label-caps">Portal Logins</p>
                            <button onClick={() => setCredsOpenFor(null)}><X size={14} /></button>
                          </div>
                          {Object.entries(PORTALS).map(([key, p]) => {
                            const u = c[p.userKey], pw = c[p.passKey];
                            return (
                              <div key={key} className="px-4 py-3 border-b border-zinc-100 text-left">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-semibold">{p.name}</span>
                                  <button onClick={() => openPortalWithCreds(c, key)} className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1">Open <ExternalLink size={10} /></button>
                                </div>
                                {u ? (
                                  <div className="space-y-1.5">
                                    <div className="flex items-center gap-1">
                                      <span className="text-[10px] uppercase text-zinc-500 w-12">User</span>
                                      <code className="flex-1 bg-zinc-50 px-2 py-1 text-xs font-mono truncate">{u}</code>
                                      <button onClick={() => copy(u, "Username")} className="p-1 hover:bg-zinc-100"><Copy size={11} /></button>
                                    </div>
                                    {pw && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-[10px] uppercase text-zinc-500 w-12">Pass</span>
                                        <code className="flex-1 bg-zinc-50 px-2 py-1 text-xs font-mono">••••••••</code>
                                        <button onClick={() => copy(pw, "Password")} className="p-1 hover:bg-zinc-100"><Copy size={11} /></button>
                                      </div>
                                    )}
                                  </div>
                                ) : <p className="text-xs text-zinc-400 italic">No credentials saved.</p>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    {(c.mobile || c.phone) && (
                      <button
                        onClick={() => {
                          const num = (c.mobile || c.phone).replace(/\D/g, "");
                          const phone = num.length === 10 ? "91" + num : num;
                          const greeting = `Hello ${c.contact_person || c.name},\n\nGreetings from TAXINPHO Office.`;
                          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(greeting)}`, "_blank", "noopener,noreferrer");
                        }}
                        title="Send WhatsApp from your number"
                        data-testid={`wa-client-${c.id}`}
                        className="p-1.5 hover:bg-green-50 text-[#25D366] mr-1"
                      >
                        <MessageCircle size={14} />
                      </button>
                    )}
                    {can(user, "clients_write") && <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-zinc-100 mr-1" data-testid={`edit-client-${c.id}`}><Pencil size={14} /></button>}
                    {can(user, "delete") && <button onClick={() => remove(c.id)} className="p-1.5 hover:bg-red-50 text-[#D92D20]"><Trash2 size={14} /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {open && (        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="client-modal">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl tracking-tight">{editing ? "Edit Client" : "New Client"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            {/* Tabs */}
            <div className="flex border-b border-zinc-200 px-6 overflow-x-auto">
              {TABS.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === t.key ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
            </div>

            <form onSubmit={save} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                {tab === "profile" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label-caps block mb-1.5 text-[10px]">Legal Name *</label>
                        <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="client-name" className="w-full border border-zinc-300 px-3 py-2 text-sm" />
                      </div>
                      {fld("Trade Name", form.trade_name, v => setForm({ ...form, trade_name: v }))}
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="label-caps block mb-1.5 text-[10px]">Constitution</label>
                        <select value={form.constitution || ""} onChange={e => setForm({ ...form, constitution: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm">
                          <option value="">Select…</option>
                          {CONSTITUTIONS.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="label-caps block mb-1.5 text-[10px]">Business Activity</label>
                        <select value={form.business_activity || ""} onChange={e => setForm({ ...form, business_activity: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm">
                          <option value="">Select…</option>
                          {BUSINESS_ACTIVITIES.map(b => <option key={b}>{b}</option>)}
                        </select>
                      </div>
                      {fld("Branch Name", form.branch_name, v => setForm({ ...form, branch_name: v }))}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {fld("PAN", form.pan, v => setForm({ ...form, pan: v }), { upper: true })}
                      {fld("GSTIN", form.gstin, v => setForm({ ...form, gstin: v }), { upper: true })}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label-caps block mb-1.5 text-[10px]">GST Registration Date</label>
                        <input type="date" value={form.gst_registration_date || ""} onChange={e => setForm({ ...form, gst_registration_date: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="label-caps block mb-1.5 text-[10px]">Commencement Date</label>
                        <input type="date" value={form.commencement_date || ""} onChange={e => setForm({ ...form, commencement_date: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {fld("Contact Person", form.contact_person, v => setForm({ ...form, contact_person: v }))}
                      {fld("Email", form.email, v => setForm({ ...form, email: v }), { type: "email" })}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {fld("Phone (landline)", form.phone, v => setForm({ ...form, phone: v }))}
                      {fld("Mobile", form.mobile, v => setForm({ ...form, mobile: v }))}
                    </div>
                  </>
                )}

                {tab === "address" && (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      {fld("Flat / Room", form.flat, v => setForm({ ...form, flat: v }))}
                      {fld("Floor", form.floor, v => setForm({ ...form, floor: v }))}
                      {fld("Premises / Building", form.premises, v => setForm({ ...form, premises: v }))}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {fld("Street", form.street, v => setForm({ ...form, street: v }))}
                      {fld("Locality / Area", form.locality, v => setForm({ ...form, locality: v }))}
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {fld("City / District", form.city, v => setForm({ ...form, city: v }))}
                      <div>
                        <label className="label-caps block mb-1.5 text-[10px]">State</label>
                        <select value={form.state || ""} onChange={e => setForm({ ...form, state: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm">
                          <option value="">Select…</option>
                          {STATES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      {fld("PIN", form.pin, v => setForm({ ...form, pin: v }))}
                    </div>
                    <div>
                      <label className="label-caps block mb-1.5 text-[10px]">Possession of Premises</label>
                      <select value={form.possession || ""} onChange={e => setForm({ ...form, possession: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm">
                        <option value="">Select…</option>
                        {["Owned", "Rented", "Leased", "Shared"].map(p => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label-caps block mb-1.5 text-[10px]">Full Address (overrides above for PDF)</label>
                      <textarea rows="2" value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm" />
                    </div>
                  </>
                )}

                {tab === "gst" && (
                  <>
                    <div className="bg-zinc-50 border border-zinc-200 p-4">
                      <p className="label-caps mb-2">Services Availed</p>
                      <p className="text-xs text-zinc-600 mb-3">Used to bifurcate the client list.</p>
                      <div className="flex flex-wrap gap-2">
                        {SERVICES.map(s => {
                          const active = (form.services || []).includes(s);
                          return <button key={s} type="button" onClick={() => toggle("services", s)} data-testid={`service-${s}`} className={`px-3 py-1.5 text-sm font-semibold border ${active ? serviceColor[s] : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>{s}</button>;
                        })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label-caps block mb-1.5 text-[10px]">Return Period</label>
                        <select value={form.return_period || "Monthly"} onChange={e => setForm({ ...form, return_period: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm">
                          {["Monthly", "Quarterly", "Annual"].map(p => <option key={p}>{p}</option>)}
                        </select>
                      </div>
                      {fld("Ward / Sector / Circle", form.ward_sector_circle, v => setForm({ ...form, ward_sector_circle: v }))}
                    </div>
                    <div className="bg-zinc-50 border border-zinc-200 p-4">
                      <p className="label-caps mb-2">Returns Applicable</p>
                      <div className="flex flex-wrap gap-2">
                        {RETURN_TYPES.map(r => {
                          const active = (form.returns_applicable || []).includes(r);
                          return <button key={r} type="button" onClick={() => toggle("returns_applicable", r)} className={`px-3 py-1.5 text-sm font-semibold border font-mono ${active ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>{r}</button>;
                        })}
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!form.is_composition} onChange={e => setForm({ ...form, is_composition: e.target.checked })} className="w-4 h-4 accent-zinc-900" />
                      <span className="text-sm">Opt for Composition Scheme</span>
                    </label>
                  </>
                )}

                {tab === "logins" && (
                  <>
                    <p className="text-xs text-zinc-600">Stored per-client; click "Open" later on the client row to launch portal with username auto-copied.</p>
                    {Object.entries(PORTALS).map(([key, p]) => (
                      <div key={key} className="bg-zinc-50 border border-zinc-200 p-3 grid grid-cols-2 gap-3">
                        <div className="col-span-2 text-sm font-semibold">{p.name}</div>
                        {fld("Username", form[p.userKey], v => setForm({ ...form, [p.userKey]: v }))}
                        <div>
                          <label className="label-caps block mb-1.5 text-[10px]">Password</label>
                          <input type="password" value={form[p.passKey] || ""} onChange={e => setForm({ ...form, [p.passKey]: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono" />
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {tab === "org" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      {fld("Group (e.g. partner name)", form.group, v => setForm({ ...form, group: v }))}
                      {fld("Sub Group", form.sub_group, v => setForm({ ...form, sub_group: v }))}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {fld("File No (internal ref)", form.file_no, v => setForm({ ...form, file_no: v }))}
                      {fld("Remark / Tag", form.remark, v => setForm({ ...form, remark: v }))}
                    </div>
                    <div>
                      <label className="label-caps block mb-1.5 text-[10px]">Internal Notes</label>
                      <textarea rows="4" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm" />
                    </div>
                  </>
                )}

                {tab === "docs" && (
                  <ClientDocs
                    clientId={editing?.id}
                    canWrite={can(user, "clients_write")}
                    canDelete={can(user, "delete")}
                  />
                )}

                {tab === "ai" && (
                  <AIClientInsight clientId={editing?.id} />
                )}
              </div>
              <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2 bg-white">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 font-semibold">Cancel</button>
                <button type="submit" data-testid="save-client" className="bg-zinc-900 text-white px-4 py-2 font-semibold hover:bg-zinc-800">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {importOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setImportOpen(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2"><FileSpreadsheet size={20} /> Bulk Import Clients</h2>
              <button onClick={() => setImportOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-[#EFF4FF] border border-[#002FA7] p-4">
                <p className="text-sm font-semibold mb-1">Step 1 — Download template</p>
                <p className="text-xs text-zinc-600 mb-3">Fill the CSV with your clients. Multi-value cells (services, returns_applicable) use <code className="bg-white px-1 font-mono">|</code> or commas as separator. <code className="bg-white px-1 font-mono">name</code> column is required.</p>
                <button onClick={downloadTemplate} data-testid="download-template" className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-blue-800">
                  <Download size={14} /> Download CSV Template
                </button>
              </div>

              <div className="border border-zinc-200 p-4">
                <p className="text-sm font-semibold mb-1">Step 2 — Upload your file</p>
                <p className="text-xs text-zinc-600 mb-3">Accepted: <code className="bg-zinc-100 px-1">.csv</code> · <code className="bg-zinc-100 px-1">.xlsx</code> · <code className="bg-zinc-100 px-1">.xls</code></p>
                <label className="block border-2 border-dashed border-zinc-300 p-6 text-center cursor-pointer hover:border-zinc-900">
                  <Upload size={20} className="mx-auto mb-2 text-zinc-500" />
                  <p className="text-sm font-semibold">{importFile ? importFile.name : "Click to choose file"}</p>
                  {importFile && <p className="text-xs text-zinc-500 mt-1">{(importFile.size / 1024).toFixed(1)} KB</p>}
                  <input type="file" accept=".csv,.xlsx,.xls" onChange={e => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }} data-testid="import-file" className="hidden" />
                </label>
              </div>

              {importResult && (
                <div className="border border-zinc-200">
                  <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                    <p className="label-caps">Import Result</p>
                  </div>
                  <div className="px-4 py-3 grid grid-cols-3 gap-3 text-center border-b border-zinc-200">
                    <div><div className="text-xs text-zinc-500">Total</div><div className="font-display font-bold text-2xl">{importResult.total}</div></div>
                    <div><div className="text-xs text-zinc-500">Inserted</div><div className="font-display font-bold text-2xl text-[#039855]">{importResult.inserted}</div></div>
                    <div><div className="text-xs text-zinc-500">Skipped</div><div className="font-display font-bold text-2xl text-[#D92D20]">{importResult.skipped}</div></div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="px-4 py-3 max-h-40 overflow-y-auto scrollbar-thin">
                      <p className="label-caps text-[10px] mb-2">Errors</p>
                      <ul className="text-xs space-y-1 font-mono">
                        {importResult.errors.map((er, i) => <li key={i} className="text-zinc-700">Row {er.row}: <span className="text-[#D92D20]">{er.error}</span></li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setImportOpen(false)} className="px-4 py-2 border border-zinc-300 font-semibold">Close</button>
                <button onClick={doImport} disabled={!importFile || importBusy} data-testid="confirm-import" className="bg-zinc-900 text-white px-4 py-2 font-semibold disabled:opacity-50 flex items-center gap-2">
                  {importBusy ? "Importing..." : <><Upload size={14} /> Import</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
